import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import ReactFlow, {
    Background,
    Controls,
    useNodesState,
    useEdgesState,
    type Node,
    type Edge,
    BackgroundVariant,
    ConnectionLineType,
    BaseEdge,
    type EdgeProps,
    getStraightPath,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { useParams } from 'react-router-dom';
import {
    Loader2,
    Server,
    Activity,
    DatabaseZap,
    CheckCircle2,
    AlertTriangle,
    AlertCircle,
    HardDrive,
    Wifi,
    X,
    Cpu,
    RefreshCw,
    History,
    ChevronRight,
    Calendar,
    Radio,
    Disc,
    Zap,
    Database,
    Layers,
    Network,
} from 'lucide-react';
import DBNode, { formatTileValue } from './DBNode';
import baseapi from '../../services/baseapi';
import { useClientLevel } from '../../hooks/useClientLevel';

// Custom Edge – glowing green for online, red dashed for offline
const DBCustomEdge = ({
    sourceX, sourceY, targetX, targetY, style = {}, markerEnd
}: EdgeProps) => {
    const [edgePath] = getStraightPath({ sourceX, sourceY, targetX, targetY });
    const isHealthy = (style as any)?.stroke !== '#ef4444';
    return (
        <>
            {/* Glow layer */}
            <path
                d={edgePath}
                fill="none"
                stroke={isHealthy ? '#22c55e' : '#ef4444'}
                strokeWidth={6}
                opacity={0.12}
                strokeLinecap="round"
            />
            <BaseEdge
                path={edgePath}
                markerEnd={markerEnd}
                style={{
                    ...style,
                    strokeWidth: 1.5,
                    opacity: 0.85,
                }}
            />
        </>
    );
};

const nodeTypes = { dbNode: DBNode };
const edgeTypes = { dbEdge: DBCustomEdge };

interface DBTopologyFlowProps {
    clusterId?: string;
}

export default function DBTopologyFlow({ clusterId: propClusterId }: DBTopologyFlowProps) {
    const { clusterId: routeClusterId } = useParams<{ clusterId: string }>();
    const activeClusterId = propClusterId || routeClusterId;

    const [loading, setLoading] = useState(true);
    const [clusterData, setClusterData] = useState<any[]>([]);
    const [clusterSummary, setClusterSummary] = useState<any>(null);
    const [selectedNode, setSelectedNode] = useState<any>(null);

    // History drawer states
    const [isHistoryOpen, setIsHistoryOpen] = useState(false);
    const [historyData, setHistoryData] = useState<any[]>([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [selectedHistoryItem, setSelectedHistoryItem] = useState<any>(null);

    // ReactFlow States
    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const reactFlowInstanceRef = useRef<any>(null);

    // Refresh States
    const [refreshTick, setRefreshTick] = useState(0);
    const [isRefreshing, setIsRefreshing] = useState(false);

    // Get client level
    const level = useClientLevel();

    // Fit view when selectedNode or drawer state changes
    useEffect(() => {
        if (reactFlowInstanceRef.current) {
            const timer = setTimeout(() => {
                reactFlowInstanceRef.current.fitView({ duration: 300, padding: 0.45, maxZoom: 1 });
            }, 350);
            return () => clearTimeout(timer);
        }
    }, [selectedNode, isHistoryOpen]);

    const handleManualRefresh = useCallback(() => {
        setRefreshTick(t => t + 1);
    }, []);

    // Reset selected node when active client level or cluster changes
    useEffect(() => {
        setSelectedNode(null);
        setSelectedHistoryItem(null);
    }, [level?.id, activeClusterId]);

    // Fetch Topology Data
    useEffect(() => {
        const fetchClusterData = async () => {
            if (!activeClusterId) {
                setClusterData([]);
                setClusterSummary(null);
                setLoading(false);
                return;
            }

            setIsRefreshing(true);
            setLoading(true);
            try {
                const params: any = { cluster_id: activeClusterId };
                if (level?.id) params.client_id = level.id;

                // Function to fetch node details from custom_nodes or cluster_nodes API
                const fetchNodesApi = async () => {
                    // try {
                    //     const res = await baseapi.get('/db_monitor_dashboard/custom_nodes', { params });
                    //     if (res?.data) return res;
                    // } catch { }
                    try {
                        const res = await baseapi.get('/db_monitor_dashboard/cluster_nodes', { params });
                        if (res?.data) return res;
                    } catch { }
                    return null;
                };

                // Call topology and nodes API concurrently
                const [topologyRes, nodesRes] = await Promise.allSettled([
                    baseapi.get('/db_monitor_dashboard/topology', { params }),
                    fetchNodesApi(),
                ]);

                let topItem: any = null;
                let rawNodes: any[] = [];
                if (topologyRes.status === 'fulfilled' && topologyRes.value?.data) {
                    const rawArr = topologyRes.value.data?.data || (Array.isArray(topologyRes.value.data) ? topologyRes.value.data : []);
                    if (Array.isArray(rawArr) && rawArr.length > 0) {
                        topItem = rawArr.find((item: any) => item.cluster_id === activeClusterId) || rawArr[0];
                        rawNodes = (topItem.nodes && topItem.nodes.length > 0)
                            ? topItem.nodes
                            : (topItem.topology?.nodes || []);
                    }
                }
                setClusterSummary(topItem);

                let clusterNodes: any[] = [];
                if (nodesRes.status === 'fulfilled' && nodesRes.value?.data) {
                    const rawData = nodesRes.value.data;
                    if (Array.isArray(rawData?.data)) {
                        clusterNodes = rawData.data;
                    } else if (Array.isArray(rawData)) {
                        clusterNodes = rawData;
                    } else if (Array.isArray(rawData?.nodes)) {
                        clusterNodes = rawData.nodes;
                    } else if (rawData?.database_overview) {
                        clusterNodes = [rawData];
                    }
                }

                const clusterMetricTiles: any[] = topItem?.metric_tiles || [];

                // Base nodes from topology, or clusterNodes if topology nodes are empty
                const targetBaseNodes = rawNodes.length > 0 ? rawNodes : clusterNodes;

                // Process nodes and attach telemetry, metric_tiles & database_overview
                const processedNodes = targetBaseNodes.map((tNode: any) => {
                    const tNodeId = tNode.node_id ?? tNode.nodeId ?? tNode.database_overview?.instance_summary?.node_id;

                    const metricNode = clusterNodes.find((cNode: any) => {
                        const cNodeId = cNode.node_id ?? cNode.nodeId ?? cNode.database_overview?.instance_summary?.node_id;
                        // Map primarily with unique node_id
                        if (tNodeId != null && cNodeId != null && String(tNodeId).trim() !== '') {
                            return String(tNodeId) === String(cNodeId);
                        }
                        // Fallback to config_id
                        if (tNode.config_id && cNode.config_id) {
                            return String(tNode.config_id) === String(cNode.config_id);
                        }
                        // Fallback to host + port or host
                        if (tNode.host && cNode.host && tNode.host === cNode.host) {
                            if (tNode.port && cNode.port) {
                                return String(tNode.port) === String(cNode.port);
                            }
                            return true;
                        }
                        return false;
                    }) || {};

                    const nodeTiles: any[] = (tNode.metric_tiles && tNode.metric_tiles.length > 0)
                        ? tNode.metric_tiles
                        : clusterMetricTiles;

                    const tileMap: Record<string, any> = {};
                    nodeTiles.forEach((tile: any) => {
                        if (tile?.key) tileMap[tile.key] = tile.value;
                    });

                    return {
                        ...metricNode,
                        ...tNode,
                        node_id: tNodeId ?? metricNode.node_id ?? metricNode.nodeId ?? tNode.node_id ?? tNode.nodeId,
                        host: tNode.host || metricNode.host || tNode.ip || metricNode.ip || '',
                        port: tNode.port || metricNode.port || null,
                        hostname: tNode.hostname || metricNode.hostname || null,
                        agent_status: metricNode.agent_status ?? tNode.agent_status ?? null,
                        last_error: metricNode.last_error ?? tNode.last_error ?? metricNode.database_overview?.instance_summary?.last_error ?? null,
                        database_overview: metricNode.database_overview || tNode.database_overview || (metricNode.instance_summary ? metricNode : null),
                        metric_tiles: nodeTiles,
                        metricTiles: nodeTiles,
                        // Preserve or map node metrics from metric_tiles or metricNode
                        tps: tNode.tps ?? metricNode.tps ?? tileMap['tps'],
                        qps: tNode.qps ?? metricNode.qps ?? tileMap['qps'],
                        connections_util_pct: tNode.connections_util_pct ?? metricNode.connections_util_pct ?? tileMap['connections_util_pct'] ?? tileMap['conn_util_pct'],
                        connections_total: tNode.connections_total ?? metricNode.connections_total ?? tileMap['connections_total'],
                        connections_max: tNode.connections_max ?? metricNode.connections_max ?? tileMap['connections_max'],
                        cache_hit_pct: tNode.cache_hit_pct ?? metricNode.cache_hit_pct ?? tileMap['cache_hit_pct'],
                        index_hit_pct: tNode.index_hit_pct ?? metricNode.index_hit_pct ?? tileMap['index_hit_pct'],
                        database_size_mb: tNode.database_size_mb ?? metricNode.database_size_mb ?? tileMap['database_size_mb'],
                        database_count: tNode.database_count ?? metricNode.database_count ?? tileMap['database_count'],
                        disk_used_pct: tNode.disk_used_pct ?? metricNode.disk_used_pct ?? tileMap['disk_used_pct'],
                        active_queries: tNode.active_queries ?? metricNode.active_queries ?? tileMap['ch_active_query_count'] ?? tileMap['active_queries'],
                        lag_sec: tNode.lag_sec ?? metricNode.lag_sec ?? tileMap['lag_sec'],
                    };
                });

                // Also include any clusterNodes that weren't present in rawNodes so their status & alerts are tracked
                const matchedNodeKeys = new Set(processedNodes.map(p => String(p.node_id ?? p.config_id ?? p.host)));
                const unmatchedClusterNodes = clusterNodes.filter(c => {
                    const cKey = String(c.node_id ?? c.config_id ?? c.host);
                    return !matchedNodeKeys.has(cKey);
                });
                const finalNodes = [...processedNodes, ...unmatchedClusterNodes];

                setClusterData(finalNodes);
            } catch (err: any) {
                console.warn('Error fetching topology:', err);
                setClusterData([]);
                setClusterSummary(null);
            } finally {
                setLoading(false);
                setIsRefreshing(false);
            }
        };

        fetchClusterData();
    }, [activeClusterId, refreshTick, level?.id]);

    // Fetch Topology History
    const fetchHistory = useCallback(async () => {
        if (!activeClusterId) return;
        setHistoryLoading(true);
        try {
            const params: any = { cluster_id: activeClusterId };
            if (level?.id) params.client_id = level.id;

            const res = await baseapi.get('/db_monitor_dashboard/topology/history', { params });
            const list = res.data?.data || (Array.isArray(res.data) ? res.data : []);
            setHistoryData(Array.isArray(list) ? list : []);
        } catch (err) {
            console.warn('Error fetching topology history:', err);
            setHistoryData([]);
        } finally {
            setHistoryLoading(false);
        }
    }, [activeClusterId, level?.id]);

    const handleToggleHistory = () => {
        if (!isHistoryOpen) {
            fetchHistory();
        }
        setIsHistoryOpen(!isHistoryOpen);
    };

    // Build the graph when data changes
    useEffect(() => {
        if (!clusterData || !Array.isArray(clusterData) || clusterData.length === 0) {
            setNodes([]);
            setEdges([]);
            return;
        }

        const newNodes: Node[] = [];
        const newEdges: Edge[] = [];

        // Primary / Master node selection logic
        const summaryPrimaryHost = clusterSummary?.primary_host || clusterSummary?.topology?.primary?.host;

        // Prioritize: 1) is_elected === true, 2) host === summaryPrimaryHost, 3) display_role === 'primary', 4) role === primary && display_role !== 'was primary'
        const primaryNode =
            clusterData.find(n => n.is_elected === true) ||
            clusterData.find(n => summaryPrimaryHost && (n.host === summaryPrimaryHost || n.config_id?.includes(summaryPrimaryHost))) ||
            clusterData.find(n => n.display_role === 'primary') ||
            clusterData.find(n => (n.role === 'primary' || n.role === 'master' || n.role === 'standalone') && n.display_role !== 'was primary' && n.state === 'online') ||
            clusterData.find(n => n.role !== 'replica' && n.display_role !== 'was primary' && n.state === 'online') ||
            clusterData[0];

        const nonPrimaryNodes = clusterData.filter(n => n !== primaryNode);

        const xSpacing = 260;
        const ySpacing = 160;

        // Add Primary Node
        if (primaryNode) {
            const isSel = (selectedNode?.node_id != null && primaryNode?.node_id != null && String(selectedNode.node_id) === String(primaryNode.node_id)) ||
                (selectedNode?.config_id && primaryNode?.config_id && String(selectedNode.config_id) === String(primaryNode.config_id)) ||
                (primaryNode.host && selectedNode?.host === primaryNode.host);
            const isTruePrimary = primaryNode.is_elected === true || primaryNode.display_role === 'primary' || (primaryNode.role === 'primary' && primaryNode.display_role !== 'was primary') || (summaryPrimaryHost && primaryNode.host === summaryPrimaryHost);

            newNodes.push({
                id: primaryNode.config_id || primaryNode.host || 'primary-1',
                type: 'dbNode',
                position: { x: 0, y: 0 },
                data: {
                    nodeId: primaryNode.node_id ? String(primaryNode.node_id) : (primaryNode.config_id || primaryNode.agent_id),
                    node_id: primaryNode.node_id,
                    dbType: primaryNode.db_type || 'opensearch',
                    role: primaryNode.role || (isTruePrimary ? 'primary' : 'replica'),
                    displayRole: primaryNode.display_role || primaryNode.role,
                    hostname: primaryNode.hostname,
                    isElected: primaryNode.is_elected,
                    roleIsMemory: primaryNode.role_is_memory,
                    upstreamLabel: primaryNode.upstream_label,
                    state: primaryNode.state || 'online',
                    host: primaryNode.host,
                    port: primaryNode.port,
                    tps: primaryNode.tps,
                    qps: primaryNode.qps,
                    connectionsUtilPct: primaryNode.connections_util_pct,
                    connectionsTotal: primaryNode.connections_total,
                    connectionsMax: primaryNode.connections_max,
                    cacheHitPct: primaryNode.cache_hit_pct,
                    indexHitPct: primaryNode.index_hit_pct,
                    dbSizeMb: primaryNode.database_size_mb,
                    databaseCount: primaryNode.database_count,
                    lagSec: primaryNode.lag_sec,
                    metricTiles: primaryNode.metric_tiles || primaryNode.metricTiles || clusterSummary?.metric_tiles || [],
                    metric_tiles: primaryNode.metric_tiles || primaryNode.metricTiles || clusterSummary?.metric_tiles || [],
                    isPrimary: isTruePrimary,
                    isSelected: isSel,
                    ...primaryNode,
                },
            });
        }

        // Add Replicas / Other Nodes
        const itemsPerRow = Math.max(3, nonPrimaryNodes.length);

        nonPrimaryNodes.forEach((node, index) => {
            const row = Math.floor(index / itemsPerRow);
            const col = index % itemsPerRow;
            const totalInRow = Math.min(itemsPerRow, nonPrimaryNodes.length - row * itemsPerRow);
            const x = (col - (totalInRow - 1) / 2) * xSpacing;
            const y = ySpacing + (row * ySpacing);
            const id = node.config_id || node.host || `replica-${index}`;
            const isSel = (selectedNode?.node_id != null && node?.node_id != null && String(selectedNode.node_id) === String(node.node_id)) ||
                (selectedNode?.config_id && node?.config_id && String(selectedNode.config_id) === String(node.config_id)) ||
                (node.host && selectedNode?.host === node.host);
            const isNodePrimary = node.is_elected === true || (node.role === 'primary' && node.display_role !== 'was primary') || (summaryPrimaryHost && node.host === summaryPrimaryHost && node.display_role !== 'was primary');

            newNodes.push({
                id: id,
                type: 'dbNode',
                position: { x, y },
                data: {
                    nodeId: node.node_id ? String(node.node_id) : (node.config_id || node.agent_id),
                    node_id: node.node_id,
                    dbType: node.db_type || 'opensearch',
                    role: node.role || 'replica',
                    displayRole: node.display_role || node.role,
                    isElected: node.is_elected,
                    roleIsMemory: node.role_is_memory,
                    upstreamLabel: node.upstream_label,
                    state: node.state || 'online',
                    host: node.host,
                    port: node.port,
                    tps: node.tps,
                    qps: node.qps,
                    connectionsUtilPct: node.connections_util_pct,
                    connectionsTotal: node.connections_total,
                    connectionsMax: node.connections_max,
                    cacheHitPct: node.cache_hit_pct,
                    indexHitPct: node.index_hit_pct,
                    dbSizeMb: node.database_size_mb,
                    databaseCount: node.database_count,
                    lagSec: node.lag_sec,
                    metricTiles: node.metric_tiles || node.metricTiles || clusterSummary?.metric_tiles || [],
                    metric_tiles: node.metric_tiles || node.metricTiles || clusterSummary?.metric_tiles || [],
                    isPrimary: isNodePrimary,
                    isSelected: isSel,
                    ...node,
                },
            });
        });

        // Edge Construction: Check for explicit edges array from API response first
        const apiEdges = clusterSummary?.edges || clusterSummary?.topology?.edges;
        if (Array.isArray(apiEdges) && apiEdges.length > 0) {
            apiEdges.forEach((edge: any, index: number) => {
                const srcNode = clusterData.find(n => n.config_id === edge.source || n.host === edge.source || edge.source?.includes(n.host));
                const tgtNode = clusterData.find(n => n.config_id === edge.target || n.host === edge.target || edge.target?.includes(n.host));
                const srcId = srcNode?.config_id || srcNode?.host || edge.source;
                const tgtId = tgtNode?.config_id || tgtNode?.host || edge.target;

                if (srcId && tgtId) {
                    const isBroken = edge.broken === true;
                    newEdges.push({
                        id: `edge-${srcId}-${tgtId}-${index}`,
                        source: srcId,
                        target: tgtId,
                        sourceHandle: 'bottom',
                        targetHandle: 'top',
                        animated: !isBroken,
                        type: 'dbEdge',
                        style: {
                            stroke: isBroken ? '#ef4444' : '#22c55e',
                            strokeDasharray: isBroken ? 'none' : '5 5',
                        }
                    });
                }
            });
        } else {
            // Dynamic fallback edge creation based on upstream_host or primary node
            nonPrimaryNodes.forEach((node, index) => {
                const id = node.config_id || node.host || `replica-${index}`;
                const parentHost = node.upstream_host || primaryNode?.host;
                const parent = clusterData.find(p => p.host === parentHost) || primaryNode;

                if (parent) {
                    const parentId = parent.config_id || parent.host || 'primary-1';
                    const isOnline = node.state === 'online' && (node.reachable ?? true);
                    newEdges.push({
                        id: `edge-${parentId}-${id}`,
                        source: parentId,
                        target: id,
                        sourceHandle: 'bottom',
                        targetHandle: 'top',
                        animated: isOnline,
                        type: 'dbEdge',
                        style: {
                            stroke: isOnline ? '#22c55e' : '#ef4444',
                            strokeDasharray: isOnline ? '5 5' : 'none',
                        }
                    });
                }
            });
        }

        setNodes(newNodes);
        setEdges(newEdges);
    }, [clusterData, clusterSummary, selectedNode, setNodes, setEdges]);

    // Handle node click
    const onNodeClick = (_: React.MouseEvent, node: Node) => {
        const clickedNodeId = node.data?.node_id ?? node.data?.nodeId ?? node.id;
        const rawNodeData = clusterData?.find(d => {
            const dNodeId = d.node_id ?? d.nodeId;
            // Map with unique node_id first
            if (clickedNodeId != null && dNodeId != null && String(clickedNodeId).trim() !== '') {
                return String(dNodeId) === String(clickedNodeId);
            }
            if (d.config_id && (d.config_id === node.id || d.config_id === node.data?.config_id)) {
                return true;
            }
            if (d.host && d.host === node.data?.host) {
                if (d.port && node.data?.port) {
                    return String(d.port) === String(node.data.port);
                }
                return true;
            }
            return false;
        });
        setSelectedNode(rawNodeData || node.data);
    };

    // Health colors for node
    const healthBadge = (h?: string, reachable = true) => {
        if (!reachable) return { bg: 'bg-red-500/10', text: 'text-red-500', border: 'border-red-500/30', label: 'UNREACHABLE' };
        if (!h) return { bg: 'bg-gray-800', text: 'text-gray-400', border: 'border-gray-700', label: 'UNKNOWN' };
        const lower = h.toLowerCase();
        if (lower === 'online' || lower === 'healthy' || lower === 'green') return { bg: 'bg-green-500/10', text: 'text-green-500', border: 'border-green-500/30', label: 'ONLINE' };
        if (lower === 'degraded' || lower === 'yellow') return { bg: 'bg-amber-500/10', text: 'text-amber-500', border: 'border-amber-500/30', label: 'DEGRADED' };
        return { bg: 'bg-red-500/10', text: 'text-red-500', border: 'border-red-500/30', label: 'OFFLINE' };
    };

    // const hc = healthBadge(selectedNode?.state, selectedNode?.reachable ?? true);
    // const utilPct = selectedNode?.connections_util_pct != null ? Number(Number(selectedNode.connections_util_pct).toFixed(1)) : 0;
    // const cacheHit = selectedNode?.cache_hit_pct != null ? Number(Number(selectedNode.cache_hit_pct).toFixed(1)) : 0;

    // Helper formatters for right-side node details
    const formatBytes = (bytes?: number) => {
        if (bytes == null || isNaN(bytes)) return '—';
        if (bytes >= 1073741824) return `${(bytes / 1073741824).toFixed(2)} GB`;
        if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1)} MB`;
        if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`;
        return `${bytes} B`;
    };

    const formatMbOrGb = (mb?: number) => {
        if (mb == null || isNaN(mb)) return '—';
        if (mb >= 1024) return `${(mb / 1024).toFixed(2)} GB`;
        return `${Number(mb.toFixed(1))} MB`;
    };

    const formatPct = (pct?: number) => {
        if (pct == null || isNaN(pct)) return '—';
        return `${Number(pct.toFixed(1))}%`;
    };

    const formatCount = (val?: number) => {
        if (val == null || isNaN(val)) return '—';
        return val.toLocaleString();
    };

    const resolveOverview = (node: any) => {
        const dbo = node?.database_overview || {};
        const lastErr = node?.last_error ?? dbo.instance_summary?.last_error ?? dbo.last_error ?? null;
        return {
            instance: {
                status: dbo.instance_summary?.status ?? dbo.instance_summary?.state ?? node?.state ?? node?.status,
                available: dbo.instance_summary?.available ?? node?.available,
                version: dbo.instance_summary?.version ?? node?.version,
                role: dbo.instance_summary?.role ?? node?.role,
                actual_role: dbo.instance_summary?.actual_role ?? node?.actual_role,
                host: dbo.instance_summary?.host ?? node?.host,
                port: dbo.instance_summary?.port ?? node?.port,
                hostname: dbo.instance_summary?.hostname ?? node?.hostname,
                ip: dbo.instance_summary?.ip ?? node?.ip,
                os: dbo.instance_summary?.os ?? node?.os,
                uptime_sec: dbo.instance_summary?.uptime_sec ?? node?.uptime_sec,
                node_id: dbo.instance_summary?.node_id ?? node?.node_id ?? node?.nodeId,
                config_id: dbo.instance_summary?.config_id ?? node?.config_id,
                collected_at: dbo.instance_summary?.collected_at ?? node?.collected_at ?? node?.last_seen_at,
                last_error: lastErr,
                agent_status: node?.agent_status ?? dbo.agent_status ?? null,
            },
            db_summary: {
                database_count: dbo.database_summary?.database_count ?? node?.database_count,
                database_size_mb: dbo.database_summary?.database_size_mb ?? node?.database_size_mb,
            },
            performance: {
                tps: dbo.performance?.tps ?? node?.tps,
                qps: dbo.performance?.qps ?? node?.qps,
                slow_queries_total: dbo.performance?.slow_queries_total ?? node?.slow_queries_total,
                cache_hit_pct: dbo.performance?.cache_hit_pct ?? node?.cache_hit_pct,
                lock_waits: dbo.performance?.lock_waits ?? node?.lock_waits,
                deadlock_count: dbo.performance?.deadlock_count ?? node?.deadlock_count,
            },
            connections: {
                connections_active: dbo.connections?.connections_active ?? node?.connections_active,
                connections_idle: dbo.connections?.connections_idle ?? node?.connections_idle,
                connections_waiting: dbo.connections?.connections_waiting ?? node?.connections_waiting,
                connections_total: dbo.connections?.connections_total ?? node?.connections_total,
                connections_max: dbo.connections?.connections_max ?? node?.connections_max,
                connections_util_pct: dbo.connections?.connections_util_pct ?? node?.connections_util_pct,
            },
            memory: {
                memory_used_mb: dbo.memory?.memory_used_mb ?? node?.memory_used_mb,
                memory_buffer_pool_mb: dbo.memory?.memory_buffer_pool_mb ?? node?.memory_buffer_pool_mb,
                host_mem_used_mb: dbo.memory?.host_mem_used_mb ?? node?.host_mem_used_mb,
                host_mem_total_mb: dbo.memory?.host_mem_total_mb ?? node?.host_mem_total_mb,
                host_mem_used_pct: dbo.memory?.host_mem_used_pct ?? node?.host_mem_used_pct,
            },
            storage: {
                database_size_mb: dbo.storage?.database_size_mb ?? node?.database_size_mb,
                disk_used_mb: dbo.storage?.disk_used_mb ?? node?.disk_used_mb,
                disk_total_mb: dbo.storage?.disk_total_mb ?? node?.disk_total_mb,
                disk_used_pct: dbo.storage?.disk_used_pct ?? node?.disk_used_pct,
            },
            host_health: {
                host_cpu_user_pct: dbo.host_health?.host_cpu_user_pct ?? node?.host_cpu_user_pct,
                host_cpu_sys_pct: dbo.host_health?.host_cpu_sys_pct ?? node?.host_cpu_sys_pct,
                host_mem_used_pct: dbo.host_health?.host_mem_used_pct ?? node?.host_mem_used_pct,
                host_mem_used_mb: dbo.host_health?.host_mem_used_mb ?? node?.host_mem_used_mb,
                host_mem_total_mb: dbo.host_health?.host_mem_total_mb ?? node?.host_mem_total_mb,
                host_net_rx_bytes: dbo.host_health?.host_net_rx_bytes ?? node?.host_net_rx_bytes,
                host_net_tx_bytes: dbo.host_health?.host_net_tx_bytes ?? node?.host_net_tx_bytes,
            },
            replication: {
                role: dbo.replication_cluster?.role ?? node?.replication_role ?? node?.role,
                actual_role: dbo.replication_cluster?.actual_role ?? node?.actual_role,
                is_replicating: dbo.replication_cluster?.is_replicating ?? node?.is_replicating,
                replication_lag_sec: dbo.replication_cluster?.replication_lag_sec ?? node?.replication_lag_sec ?? node?.lag_sec,
                replication_lag_bytes: dbo.replication_cluster?.replication_lag_bytes ?? node?.replication_lag_bytes,
                lag_sec: dbo.replication_cluster?.lag_sec ?? node?.lag_sec,
                cluster_health: dbo.replication_cluster?.cluster_health ?? node?.cluster_health,
            },
            mongodb: dbo.mongodb_metrics || ((node?.db_type?.toLowerCase() === 'mongodb' || dbo.mongodb_metrics) ? {
                tps: dbo.mongodb_metrics?.tps ?? node?.tps,
                qps: dbo.mongodb_metrics?.qps ?? node?.qps,
                cache_hit_pct: dbo.mongodb_metrics?.cache_hit_pct ?? node?.cache_hit_pct,
                connections_active: dbo.mongodb_metrics?.connections_active ?? node?.connections_active,
                connections_idle: dbo.mongodb_metrics?.connections_idle ?? node?.connections_idle,
                connections_waiting: dbo.mongodb_metrics?.connections_waiting ?? node?.connections_waiting,
                connections_total: dbo.mongodb_metrics?.connections_total ?? node?.connections_total,
                slow_queries_total: dbo.mongodb_metrics?.slow_queries_total ?? node?.slow_queries_total,
                lock_waits: dbo.mongodb_metrics?.lock_waits ?? node?.lock_waits,
                deadlock_count: dbo.mongodb_metrics?.deadlock_count ?? node?.deadlock_count,
            } : null),
        };
    };

    // Helper to format uptime
    const formatUptime = (sec?: number) => {
        if (!sec) return '—';
        const d = Math.floor(sec / 86400);
        const h = Math.floor((sec % 86400) / 3600);
        const m = Math.floor((sec % 3600) / 60);
        if (d > 0) return `${d}d ${h}h`;
        return `${h}h ${m}m`;
    };

    // Helper for date formatting
    const formatDate = (dateStr?: string) => {
        if (!dateStr) return '—';
        const d = new Date(dateStr);
        return isNaN(d.getTime()) ? dateStr : d.toLocaleString([], {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        });
    };

    // Extract nodes that have an agent_status with a non-null message
    const agentStatusAlerts = useMemo(() => {
        if (!clusterData || !Array.isArray(clusterData)) return [];
        return clusterData
            .filter((node: any) => {
                const msg = node?.agent_status?.message;
                return msg != null && String(msg).trim() !== '';
            })
            .map((node: any) => {
                const hostName = node.hostname || node.host_name || node.database_overview?.instance_summary?.hostname || node.host || node.ip || (node.config_id ? node.config_id.split('@')[1]?.split(':')[0] : '') || (node.node_id ? `Node ${node.node_id}` : 'Node');
                const hostIp = node.host || node.ip || '';
                const port = node.port || (node.config_id ? node.config_id.split(':')[1] : null);
                const displayIp = port && hostIp ? `${hostIp}:${port}` : hostIp;
                const status = node.agent_status?.status || (node.agent_status?.offline ? 'offline' : 'online');
                const isOffline = Boolean(node.agent_status?.offline || String(status).toLowerCase() === 'offline');
                const uniqueKey = String(node.node_id ?? node.config_id ?? node.host ?? Math.random());
                return {
                    id: uniqueKey,
                    hostName,
                    hostIp: displayIp,
                    message: String(node.agent_status.message).trim(),
                    status,
                    isOffline,
                    rawNode: node,
                };
            });
    }, [clusterData]);

    return (
        <div className="w-full h-full min-h-[500px] bg-white dark:bg-gray-900/60 border border-gray-200 dark:border-gray-700/60 rounded-xl text-gray-900 dark:text-white font-sans flex flex-row relative overflow-hidden shadow-sm dark:shadow-xl">
            {/* CENTER PANEL: TOPOLOGY CANVAS */}
            <div className="flex-1 relative flex flex-col h-full bg-slate-50/50 dark:bg-gray-900 min-w-0">
                {/* Top Bar with History & Refresh buttons */}
                <div className="flex items-center justify-between px-4 py-2 bg-white/80 dark:bg-gray-800/40 border-b border-gray-200 dark:border-gray-700/60 shrink-0 backdrop-blur-sm z-10 gap-2">
                    <div className="flex items-center gap-2 truncate min-w-0">
                        <span className="text-xs text-gray-800 dark:text-gray-200 uppercase tracking-wider font-bold truncate">
                            {clusterSummary?.cluster_name ? `${clusterSummary.cluster_name} Node Topology` : 'Database Node Topology Map'}
                        </span>
                        {clusterSummary?.status && (
                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${clusterSummary.status === 'healthy' ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30' : 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/30'
                                }`}>
                                {clusterSummary.status}
                            </span>
                        )}
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                        {/* History Button */}
                        <button
                            onClick={handleToggleHistory}
                            className={`flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide border rounded transition-all duration-200 cursor-pointer ${isHistoryOpen
                                ? 'bg-orange-600 text-white border-orange-500 shadow-md'
                                : 'bg-white hover:bg-gray-50 dark:bg-gray-800 dark:hover:bg-gray-700 border-gray-300 dark:border-gray-700 text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white shadow-sm'
                                }`}
                        >
                            <History className="w-3.5 h-3.5 text-orange-600 dark:text-orange-400" />
                            History
                        </button>

                        {/* Refresh Button */}
                        <button
                            onClick={handleManualRefresh}
                            disabled={isRefreshing}
                            className="flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide bg-white hover:bg-gray-50 dark:bg-gray-800 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-700 text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white rounded transition-all duration-200 disabled:opacity-50 cursor-pointer shadow-sm"
                        >
                            <RefreshCw className={`w-3 h-3 ${isRefreshing ? 'animate-spin' : ''}`} />
                            Refresh
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div className="flex-1 flex items-center justify-center">
                        <div className="flex flex-col items-center gap-4">
                            <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
                            <span className="text-sm text-gray-500 dark:text-gray-400 font-bold uppercase tracking-widest">Loading Topology...</span>
                        </div>
                    </div>
                ) : !clusterData || clusterData.length === 0 ? (
                    <div className="flex-1 flex items-center justify-center">
                        <div className="text-gray-500 dark:text-gray-400 text-sm font-bold uppercase tracking-widest">No Topology Data Found</div>
                    </div>
                ) : (
                    <div className="relative flex-1 w-full h-full min-h-0">
                        {/* Top-Left Canvas Agent Status Messages (Stacked One Below Another) */}
                        {agentStatusAlerts.length > 0 && (
                            <div className="absolute top-2.5 left-2.5 z-20 flex flex-col gap-1.5 max-w-[280px] pointer-events-none max-h-[60%] overflow-y-auto custom-scroll pr-1">
                                {agentStatusAlerts.map((alert: any) => (
                                    <div
                                        key={alert.id}
                                        onClick={() => setSelectedNode(alert.rawNode)}
                                        className="pointer-events-auto bg-white/95 dark:bg-gray-900/95 border border-amber-300/80 dark:border-amber-600/60 shadow-md backdrop-blur-md rounded-lg px-2.5 py-1.5 transition-all duration-150 hover:border-amber-500 dark:hover:border-amber-400 cursor-pointer group"
                                        title="Click to view node details"
                                    >
                                        <div className="flex items-center gap-1.5 min-w-0">
                                            <AlertTriangle className="w-3 h-3 text-amber-500 shrink-0" />
                                            <span className="font-mono text-[11px] font-bold text-gray-900 dark:text-white truncate" title={alert.hostName}>
                                                {alert.hostName}
                                            </span>
                                            {alert.hostIp && alert.hostIp !== alert.hostName && (
                                                <span className="text-[10px] text-gray-400 font-mono shrink-0 truncate">
                                                    ({alert.hostIp})
                                                </span>
                                            )}
                                            {alert.status && (
                                                <span className={`px-1 py-0.2 rounded text-[8px] uppercase font-bold border ml-auto shrink-0 ${
                                                    alert.isOffline
                                                        ? 'bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-400 border-rose-200 dark:border-rose-800'
                                                        : 'bg-amber-100 dark:bg-amber-950/60 text-amber-800 dark:text-amber-300 border-amber-300 dark:border-amber-800'
                                                }`}>
                                                    {alert.status}
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-[10px] text-amber-900 dark:text-amber-200/90 leading-tight break-words font-sans mt-0.5 line-clamp-2">
                                            {alert.message}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        )}

                        <ReactFlow
                            nodes={nodes}
                            edges={edges}
                            onNodesChange={onNodesChange}
                            onEdgesChange={onEdgesChange}
                            onNodeClick={onNodeClick}
                            onInit={(instance) => { reactFlowInstanceRef.current = instance; }}
                            nodeTypes={nodeTypes}
                            edgeTypes={edgeTypes}
                            nodeOrigin={[0.5, 0.5]}
                            fitView
                            fitViewOptions={{ padding: 0.45, maxZoom: 1 }}
                            minZoom={0.1}
                            maxZoom={2}
                            connectionLineType={ConnectionLineType.Straight}
                            proOptions={{ hideAttribution: true }}
                            style={{ background: 'transparent' }}
                        >
                            <Background variant={BackgroundVariant.Dots} gap={24} size={1} color="#94a3b8" className="opacity-40 dark:opacity-20" />
                            <Controls className="!bg-white dark:!bg-gray-800 !border-gray-300 dark:!border-gray-700 !fill-gray-600 dark:!fill-gray-400 shadow-md" position="bottom-left" />
                        </ReactFlow>
                    </div>
                )}
            </div>

            {/* RIGHT PANEL: NODE DETAILS */}
            {selectedNode && (() => {
                const overview = resolveOverview(selectedNode);
                const { instance, db_summary, performance, connections, memory, storage, host_health, replication, mongodb } = overview;
                const drawerTiles: any[] = selectedNode.metric_tiles || selectedNode.metricTiles || clusterSummary?.metric_tiles || [];
                const nodeHc = healthBadge(instance.status, instance.available ?? selectedNode.reachable ?? true);

                return (
                    <div className="w-[360px] h-full flex flex-col bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-800 overflow-y-auto custom-scroll shadow-2xl z-20 transition-all duration-300 shrink-0">
                        {/* Sticky Header */}
                        <div className="flex items-center justify-between px-3.5 py-2.5 border-b border-gray-200 dark:border-gray-800 bg-gray-50/90 dark:bg-gray-800/80 backdrop-blur-md sticky top-0 z-10">
                            <h2 className="text-xs font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wider flex items-center gap-1.5">
                                <Server className="w-3.5 h-3.5 text-orange-600 dark:text-orange-400" /> Node Details
                            </h2>
                            <button
                                onClick={() => setSelectedNode(null)}
                                className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white cursor-pointer"
                            >
                                <X className="w-3.5 h-3.5" />
                            </button>
                        </div>

                        <div className="flex flex-col p-3 gap-3">
                            {/* Header Summary Card */}
                            <div className="bg-gray-50 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/50 rounded-lg p-2.5 space-y-2">
                                <div className="flex justify-between items-start">
                                    <div className="min-w-0 pr-2">
                                        <h3 className="text-xs font-bold text-gray-900 dark:text-white font-mono break-all leading-tight" title={instance.host || selectedNode.host}>
                                            {instance.host || selectedNode.host || selectedNode.nodeId || 'Unknown Node'}:{instance.port || selectedNode.port || '—'}
                                        </h3>
                                        {instance.hostname && (
                                            <span className="text-[10px] text-gray-500 dark:text-gray-400 font-mono block mt-0.5 truncate" title={instance.hostname}>
                                                {instance.hostname}
                                            </span>
                                        )}
                                    </div>
                                    <span className={`flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-bold border ${nodeHc.bg} ${nodeHc.text} ${nodeHc.border} uppercase shrink-0`}>
                                        {nodeHc.label === 'ONLINE' && <CheckCircle2 className="w-2.5 h-2.5" />}
                                        {nodeHc.label === 'DEGRADED' && <AlertTriangle className="w-2.5 h-2.5" />}
                                        {(nodeHc.label === 'OFFLINE' || nodeHc.label === 'UNREACHABLE') && <AlertCircle className="w-2.5 h-2.5" />}
                                        {nodeHc.label}
                                    </span>
                                </div>

                                <div className="flex flex-wrap gap-1.5 pt-1">
                                    <span className={`px-1.5 py-0.5 rounded text-[9px] uppercase font-bold border ${instance.role === 'primary'
                                        ? 'text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-500/15 border-blue-200 dark:border-blue-500/30'
                                        : 'text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-500/15 border-purple-200 dark:border-purple-500/30'
                                        }`}>
                                        Role: {instance.role || 'standalone'}
                                    </span>
                                    {instance.actual_role && instance.actual_role !== instance.role && (
                                        <span className="px-1.5 py-0.5 rounded text-[9px] uppercase font-bold border text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/15 border-amber-200 dark:border-amber-500/30">
                                            Actual: {instance.actual_role}
                                        </span>
                                    )}
                                    {instance.available != null && (
                                        <span className={`px-1.5 py-0.5 rounded text-[9px] uppercase font-bold border ${instance.available ? 'text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-500/15 border-emerald-200 dark:border-emerald-500/30' : 'text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-500/15 border-red-200 dark:border-red-500/30'
                                            }`}>
                                            Available: {instance.available ? 'YES' : 'NO'}
                                        </span>
                                    )}
                                    {instance.version && (
                                        <span className="px-1.5 py-0.5 rounded text-[9px] font-mono border text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700/30 border-gray-200 dark:border-gray-600/40">
                                            v{instance.version}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Last Error Banner if present */}
                            {instance.last_error != null && instance.last_error !== '' && (
                                <div className="bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800/60 rounded-lg p-2.5 flex items-start gap-2 shadow-sm">
                                    <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
                                    <div className="min-w-0 flex-1">
                                        <span className="text-[10px] font-bold text-red-700 dark:text-red-300 uppercase tracking-wider block">
                                            Last Error
                                        </span>
                                        <div className="text-[11px] text-red-600 dark:text-red-400 font-mono break-all mt-0.5 whitespace-pre-wrap">
                                            {typeof instance.last_error === 'string'
                                                ? instance.last_error
                                                : JSON.stringify(instance.last_error, null, 2)}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Agent Status Message Banner if present */}
                            {instance.agent_status?.message != null && String(instance.agent_status.message).trim() !== '' && (
                                <div className="bg-amber-50 dark:bg-amber-950/40 border border-amber-300 dark:border-amber-800/60 rounded-lg p-2.5 flex items-start gap-2 shadow-sm">
                                    <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center justify-between gap-1">
                                            <span className="text-[10px] font-bold text-amber-800 dark:text-amber-300 uppercase tracking-wider block">
                                                Agent Alert
                                            </span>
                                            {instance.agent_status.status && (
                                                <span className={`px-1.5 py-0.2 rounded text-[9px] uppercase font-bold border ${
                                                    instance.agent_status.offline
                                                        ? 'bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-400 border-rose-200 dark:border-rose-800'
                                                        : 'bg-amber-100 dark:bg-amber-900/60 text-amber-800 dark:text-amber-200 border-amber-300 dark:border-amber-700'
                                                }`}>
                                                    {instance.agent_status.status}
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-[11px] text-amber-800 dark:text-amber-300 font-mono break-all mt-0.5 whitespace-pre-wrap">
                                            {instance.agent_status.message}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Metric Grid Tiles if available */}
                            {drawerTiles && drawerTiles.length > 0 && (
                                <div className="grid grid-cols-2 gap-2">
                                    {drawerTiles.map((tile: any, idx: number) => {
                                        const formattedVal = formatTileValue(tile);
                                        const lowerKey = (tile.key || '').toLowerCase();
                                        const isQpsOrTps = lowerKey.includes('tps') || lowerKey.includes('qps');
                                        const isSize = lowerKey.includes('size');
                                        const isConn = lowerKey.includes('conn');
                                        const isCache = lowerKey.includes('cache');
                                        const isDisk = lowerKey.includes('disk');

                                        const IconComp = isQpsOrTps ? Activity :
                                            isDisk ? Disc :
                                                isSize ? HardDrive :
                                                    isConn ? Wifi :
                                                        isCache ? DatabaseZap : Zap;

                                        const iconCol = isQpsOrTps ? 'text-blue-600 dark:text-blue-400' :
                                            isDisk ? 'text-amber-600 dark:text-amber-400' :
                                                isSize ? 'text-purple-600 dark:text-purple-400' :
                                                    isConn ? 'text-cyan-600 dark:text-cyan-400' :
                                                        isCache ? 'text-emerald-600 dark:text-emerald-400' : 'text-teal-600 dark:text-teal-400';

                                        return (
                                            <div key={tile.key || idx} className="bg-gray-50 dark:bg-gray-800/30 border border-gray-200 dark:border-gray-700/50 rounded-lg p-2 flex flex-col justify-between shadow-sm">
                                                <div className="text-[9px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1">
                                                    <IconComp className={`w-3 h-3 ${iconCol}`} />
                                                    <span className="truncate">{tile.label || tile.key}</span>
                                                </div>
                                                <div className="text-base font-bold text-gray-900 dark:text-gray-100 font-mono mt-0.5">
                                                    {formattedVal}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Performance Section */}
                            {(performance.tps != null || performance.qps != null || performance.slow_queries_total != null || performance.cache_hit_pct != null || performance.lock_waits != null || performance.deadlock_count != null) && (
                                <div className="bg-gray-50 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/50 rounded-lg p-2.5 space-y-2">
                                    <div className="text-[10px] font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider flex items-center gap-1.5 border-b border-gray-200 dark:border-gray-700/50 pb-1.5">
                                        <Activity className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" /> Performance
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <div className="bg-white dark:bg-gray-900/60 border border-gray-200 dark:border-gray-800 rounded-md p-2">
                                            <span className="text-[9px] uppercase text-gray-500 dark:text-gray-400 font-bold block">TPS</span>
                                            <span className="text-sm font-bold font-mono text-blue-600 dark:text-blue-400">{formatCount(performance.tps)}</span>
                                        </div>
                                        <div className="bg-white dark:bg-gray-900/60 border border-gray-200 dark:border-gray-800 rounded-md p-2">
                                            <span className="text-[9px] uppercase text-gray-500 dark:text-gray-400 font-bold block">QPS</span>
                                            <span className="text-sm font-bold font-mono text-cyan-600 dark:text-cyan-400">{formatCount(performance.qps)}</span>
                                        </div>
                                        <div className="bg-white dark:bg-gray-900/60 border border-gray-200 dark:border-gray-800 rounded-md p-2">
                                            <span className="text-[9px] uppercase text-gray-500 dark:text-gray-400 font-bold block">Cache Hit</span>
                                            <span className="text-sm font-bold font-mono text-emerald-600 dark:text-emerald-400">{formatPct(performance.cache_hit_pct)}</span>
                                        </div>
                                        <div className="bg-white dark:bg-gray-900/60 border border-gray-200 dark:border-gray-800 rounded-md p-2">
                                            <span className="text-[9px] uppercase text-gray-500 dark:text-gray-400 font-bold block">Slow Queries</span>
                                            <span className={`text-sm font-bold font-mono ${performance.slow_queries_total > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-gray-700 dark:text-gray-300'}`}>
                                                {formatCount(performance.slow_queries_total)}
                                            </span>
                                        </div>
                                        <div className="bg-white dark:bg-gray-900/60 border border-gray-200 dark:border-gray-800 rounded-md p-2">
                                            <span className="text-[9px] uppercase text-gray-500 dark:text-gray-400 font-bold block">Lock Waits</span>
                                            <span className="text-sm font-bold font-mono text-gray-700 dark:text-gray-300">{formatCount(performance.lock_waits)}</span>
                                        </div>
                                        <div className="bg-white dark:bg-gray-900/60 border border-gray-200 dark:border-gray-800 rounded-md p-2">
                                            <span className="text-[9px] uppercase text-gray-500 dark:text-gray-400 font-bold block">Deadlocks</span>
                                            <span className={`text-sm font-bold font-mono ${performance.deadlock_count > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-700 dark:text-gray-300'}`}>
                                                {formatCount(performance.deadlock_count)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Connections Section */}
                            {(connections.connections_total != null || connections.connections_active != null || connections.connections_util_pct != null) && (
                                <div className="bg-gray-50 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/50 rounded-lg p-2.5 space-y-2">
                                    <div className="flex justify-between items-center border-b border-gray-200 dark:border-gray-700/50 pb-1.5">
                                        <div className="text-[10px] font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider flex items-center gap-1.5">
                                            <Wifi className="w-3.5 h-3.5 text-cyan-600 dark:text-cyan-400" /> Connections
                                        </div>
                                        {connections.connections_util_pct != null && (
                                            <span className={`text-[10px] font-mono font-bold px-1.5 py-0.5 rounded ${connections.connections_util_pct > 80 ? 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400' :
                                                connections.connections_util_pct > 60 ? 'bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400' :
                                                    'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400'
                                                }`}>
                                                {formatPct(connections.connections_util_pct)} utilized
                                            </span>
                                        )}
                                    </div>
                                    {connections.connections_util_pct != null && (
                                        <div className="w-full bg-gray-200 dark:bg-gray-950 rounded-full h-1.5 overflow-hidden">
                                            <div
                                                className={`h-full transition-all duration-300 ${connections.connections_util_pct > 80 ? 'bg-red-500' :
                                                    connections.connections_util_pct > 60 ? 'bg-amber-400' : 'bg-cyan-500 dark:bg-cyan-400'
                                                    }`}
                                                style={{ width: `${Math.min(connections.connections_util_pct || 0, 100)}%` }}
                                            />
                                        </div>
                                    )}
                                    <div className="grid grid-cols-3 gap-2 pt-1 text-center">
                                        <div className="bg-white dark:bg-gray-900/60 border border-gray-200 dark:border-gray-800 rounded p-1.5">
                                            <span className="text-[9px] uppercase text-gray-500 dark:text-gray-400 block">Active</span>
                                            <span className="text-xs font-bold font-mono text-emerald-600 dark:text-emerald-400">{formatCount(connections.connections_active)}</span>
                                        </div>
                                        <div className="bg-white dark:bg-gray-900/60 border border-gray-200 dark:border-gray-800 rounded p-1.5">
                                            <span className="text-[9px] uppercase text-gray-500 dark:text-gray-400 block">Idle</span>
                                            <span className="text-xs font-bold font-mono text-gray-700 dark:text-gray-300">{formatCount(connections.connections_idle)}</span>
                                        </div>
                                        <div className="bg-white dark:bg-gray-900/60 border border-gray-200 dark:border-gray-800 rounded p-1.5">
                                            <span className="text-[9px] uppercase text-gray-500 dark:text-gray-400 block">Waiting</span>
                                            <span className={`text-xs font-bold font-mono ${connections.connections_waiting > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-gray-700 dark:text-gray-300'}`}>
                                                {formatCount(connections.connections_waiting)}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex justify-between items-center text-[11px] pt-1">
                                        <span className="text-gray-500 dark:text-gray-400">Total / Max Limit</span>
                                        <span className="font-mono text-gray-800 dark:text-gray-200 font-bold">
                                            {formatCount(connections.connections_total)} / {formatCount(connections.connections_max)}
                                        </span>
                                    </div>
                                </div>
                            )}

                            {/* Storage & Database Summary Section */}
                            {(storage.database_size_mb != null || db_summary.database_count != null || storage.disk_used_mb != null) && (
                                <div className="bg-gray-50 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/50 rounded-lg p-2.5 space-y-2">
                                    <div className="flex justify-between items-center border-b border-gray-200 dark:border-gray-700/50 pb-1.5">
                                        <div className="text-[10px] font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider flex items-center gap-1.5">
                                            <HardDrive className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" /> Storage & Databases
                                        </div>
                                        {storage.disk_used_pct != null && (
                                            <span className="text-[10px] font-mono text-gray-500 dark:text-gray-400">
                                                {formatPct(storage.disk_used_pct)} disk used
                                            </span>
                                        )}
                                    </div>
                                    {storage.disk_used_pct != null && (
                                        <div className="w-full bg-gray-200 dark:bg-gray-950 rounded-full h-1.5 overflow-hidden">
                                            <div
                                                className={`h-full transition-all duration-300 ${storage.disk_used_pct > 85 ? 'bg-red-500' :
                                                    storage.disk_used_pct > 70 ? 'bg-amber-400' : 'bg-purple-500'
                                                    }`}
                                                style={{ width: `${Math.min(storage.disk_used_pct || 0, 100)}%` }}
                                            />
                                        </div>
                                    )}
                                    <div className="grid grid-cols-2 gap-2 text-[11px]">
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-500 dark:text-gray-400">DB Size:</span>
                                            <span className="font-mono font-bold text-purple-700 dark:text-purple-300">{formatMbOrGb(storage.database_size_mb ?? db_summary.database_size_mb)}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-500 dark:text-gray-400">Databases:</span>
                                            <span className="font-mono font-bold text-gray-800 dark:text-gray-200">{formatCount(db_summary.database_count)}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-500 dark:text-gray-400">Disk Used:</span>
                                            <span className="font-mono text-gray-700 dark:text-gray-300">{formatMbOrGb(storage.disk_used_mb)}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-500 dark:text-gray-400">Disk Total:</span>
                                            <span className="font-mono text-gray-700 dark:text-gray-300">{formatMbOrGb(storage.disk_total_mb)}</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Memory Section */}
                            {(memory.memory_used_mb != null || memory.host_mem_used_mb != null || memory.memory_buffer_pool_mb != null) && (
                                <div className="bg-gray-50 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/50 rounded-lg p-2.5 space-y-2">
                                    <div className="flex justify-between items-center border-b border-gray-200 dark:border-gray-700/50 pb-1.5">
                                        <div className="text-[10px] font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider flex items-center gap-1.5">
                                            <Layers className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" /> Memory
                                        </div>
                                        {memory.host_mem_used_pct != null && (
                                            <span className="text-[10px] font-mono text-emerald-600 dark:text-emerald-400">
                                                {formatPct(memory.host_mem_used_pct)} Host Mem
                                            </span>
                                        )}
                                    </div>
                                    {memory.host_mem_used_pct != null && (
                                        <div className="w-full bg-gray-200 dark:bg-gray-950 rounded-full h-1.5 overflow-hidden">
                                            <div
                                                className="h-full bg-emerald-500 transition-all duration-300"
                                                style={{ width: `${Math.min(memory.host_mem_used_pct || 0, 100)}%` }}
                                            />
                                        </div>
                                    )}
                                    <div className="grid grid-cols-2 gap-2 text-[11px]">
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-500 dark:text-gray-400">Mem Used:</span>
                                            <span className="font-mono font-bold text-emerald-700 dark:text-emerald-300">{formatMbOrGb(memory.memory_used_mb)}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-500 dark:text-gray-400">Buffer Pool:</span>
                                            <span className="font-mono text-gray-700 dark:text-gray-300">{formatMbOrGb(memory.memory_buffer_pool_mb)}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-500 dark:text-gray-400">Host Used:</span>
                                            <span className="font-mono text-gray-700 dark:text-gray-300">{formatMbOrGb(memory.host_mem_used_mb)}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-500 dark:text-gray-400">Host Total:</span>
                                            <span className="font-mono text-gray-700 dark:text-gray-300">{formatMbOrGb(memory.host_mem_total_mb)}</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Host Health & Network Section */}
                            {(host_health.host_cpu_user_pct != null || host_health.host_net_rx_bytes != null) && (
                                <div className="bg-gray-50 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/50 rounded-lg p-2.5 space-y-2">
                                    <div className="text-[10px] font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider flex items-center gap-1.5 border-b border-gray-200 dark:border-gray-700/50 pb-1.5">
                                        <Cpu className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" /> Host Health & Network
                                    </div>
                                    <div className="grid grid-cols-2 gap-2 text-[11px]">
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-500 dark:text-gray-400">CPU User:</span>
                                            <span className="font-mono text-gray-800 dark:text-gray-200 font-bold">{formatPct(host_health.host_cpu_user_pct)}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-500 dark:text-gray-400">CPU System:</span>
                                            <span className="font-mono text-gray-800 dark:text-gray-200 font-bold">{formatPct(host_health.host_cpu_sys_pct)}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-500 dark:text-gray-400">Net RX:</span>
                                            <span className="font-mono text-cyan-700 dark:text-cyan-300">{formatBytes(host_health.host_net_rx_bytes)}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-500 dark:text-gray-400">Net TX:</span>
                                            <span className="font-mono text-cyan-700 dark:text-cyan-300">{formatBytes(host_health.host_net_tx_bytes)}</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Replication & Cluster Section */}
                            {(replication.is_replicating != null || replication.replication_lag_sec != null || replication.cluster_health != null || replication.actual_role) && (
                                <div className="bg-gray-50 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/50 rounded-lg p-2.5 space-y-2">
                                    <div className="text-[10px] font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider flex items-center gap-1.5 border-b border-gray-200 dark:border-gray-700/50 pb-1.5">
                                        <Network className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" /> Replication & Cluster
                                    </div>
                                    <div className="space-y-1.5 text-[11px]">
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-500 dark:text-gray-400">Role</span>
                                            <span className="font-mono font-bold uppercase text-indigo-700 dark:text-indigo-300">{replication.role || 'standalone'}</span>
                                        </div>
                                        {replication.actual_role && (
                                            <div className="flex justify-between items-center">
                                                <span className="text-gray-500 dark:text-gray-400">Actual Role</span>
                                                <span className="font-mono text-gray-700 dark:text-gray-300 uppercase">{replication.actual_role}</span>
                                            </div>
                                        )}
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-500 dark:text-gray-400">Is Replicating</span>
                                            <span className={`font-bold text-[10px] ${replication.is_replicating ? 'text-emerald-600 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'}`}>
                                                {replication.is_replicating === true ? 'YES' : replication.is_replicating === false ? 'NO' : '—'}
                                            </span>
                                        </div>
                                        {(replication.replication_lag_sec != null || replication.lag_sec != null) && (
                                            <div className="flex justify-between items-center">
                                                <span className="text-gray-500 dark:text-gray-400">Replication Lag</span>
                                                <span className="font-mono text-amber-600 dark:text-amber-400 font-bold">
                                                    {replication.replication_lag_sec != null ? `${replication.replication_lag_sec}s` : (replication.lag_sec != null ? `${replication.lag_sec}s` : '0s')}
                                                </span>
                                            </div>
                                        )}
                                        {replication.replication_lag_bytes != null && (
                                            <div className="flex justify-between items-center">
                                                <span className="text-gray-500 dark:text-gray-400">Lag Bytes</span>
                                                <span className="font-mono text-gray-700 dark:text-gray-300">{formatBytes(replication.replication_lag_bytes)}</span>
                                            </div>
                                        )}
                                        {replication.cluster_health && (
                                            <div className="flex justify-between items-center">
                                                <span className="text-gray-500 dark:text-gray-400">Cluster Health</span>
                                                <span className="font-mono text-emerald-600 dark:text-emerald-400 font-bold uppercase">{replication.cluster_health}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* MongoDB Metrics Section (if present) */}
                            {mongodb && (mongodb.tps != null || mongodb.connections_active != null || mongodb.cache_hit_pct != null) && (
                                <div className="bg-gray-50 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/50 rounded-lg p-2.5 space-y-2">
                                    <div className="text-[10px] font-bold text-green-600 dark:text-green-400 uppercase tracking-wider flex items-center gap-1.5 border-b border-gray-200 dark:border-gray-700/50 pb-1.5">
                                        <Database className="w-3.5 h-3.5 text-green-600 dark:text-green-400" /> MongoDB Metrics
                                    </div>
                                    <div className="grid grid-cols-2 gap-2 text-[11px]">
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-500 dark:text-gray-400">TPS:</span>
                                            <span className="font-mono text-blue-600 dark:text-blue-400 font-bold">{formatCount(mongodb.tps)}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-500 dark:text-gray-400">QPS:</span>
                                            <span className="font-mono text-cyan-600 dark:text-cyan-400 font-bold">{formatCount(mongodb.qps)}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-500 dark:text-gray-400">Cache Hit:</span>
                                            <span className="font-mono text-emerald-600 dark:text-emerald-400 font-bold">{formatPct(mongodb.cache_hit_pct)}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-500 dark:text-gray-400">Conns Active:</span>
                                            <span className="font-mono text-green-600 dark:text-green-400">{formatCount(mongodb.connections_active)}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-500 dark:text-gray-400">Conns Idle:</span>
                                            <span className="font-mono text-gray-700 dark:text-gray-300">{formatCount(mongodb.connections_idle)}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-500 dark:text-gray-400">Conns Waiting:</span>
                                            <span className="font-mono text-amber-600 dark:text-amber-400">{formatCount(mongodb.connections_waiting)}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-500 dark:text-gray-400">Conns Total:</span>
                                            <span className="font-mono text-gray-800 dark:text-gray-200">{formatCount(mongodb.connections_total)}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-500 dark:text-gray-400">Slow Queries:</span>
                                            <span className="font-mono text-amber-600 dark:text-amber-400">{formatCount(mongodb.slow_queries_total)}</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Instance Specs & Metadata Card */}
                            <div className="bg-gray-50 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/50 rounded-lg p-2.5 space-y-1.5 text-[11px]">
                                <div className="text-[10px] font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider flex items-center gap-1.5 border-b border-gray-200 dark:border-gray-700/50 pb-1.5 mb-1">
                                    <Radio className="w-3.5 h-3.5 text-cyan-600 dark:text-cyan-400" /> Instance & Specs
                                </div>
                                {instance.config_id && (
                                    <div>
                                        <span className="text-gray-500 dark:text-gray-400 text-[9px] uppercase block">Config ID</span>
                                        <span className="font-mono text-gray-700 dark:text-gray-300 text-[10px] break-all">{instance.config_id}</span>
                                    </div>
                                )}
                                {instance.node_id && (
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-500 dark:text-gray-400">Node ID</span>
                                        <span className="font-mono text-gray-800 dark:text-gray-200 font-bold text-[10px]">{instance.node_id}</span>
                                    </div>
                                )}
                                {selectedNode.agent_id && (
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-500 dark:text-gray-400">Agent ID</span>
                                        <span className="font-mono text-gray-800 dark:text-gray-200 font-bold text-[10px]">{selectedNode.agent_id}</span>
                                    </div>
                                )}
                                {selectedNode.db_type && (
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-500 dark:text-gray-400">DB Type</span>
                                        <span className="font-mono text-orange-600 dark:text-orange-400 uppercase font-bold text-[10px]">{selectedNode.db_type}</span>
                                    </div>
                                )}
                                {instance.ip && (
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-500 dark:text-gray-400">IP</span>
                                        <span className="font-mono text-gray-700 dark:text-gray-300 text-[10px]">{instance.ip}</span>
                                    </div>
                                )}
                                {instance.os && (
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-500 dark:text-gray-400">OS</span>
                                        <span className="font-mono text-gray-700 dark:text-gray-300 text-[10px]">{instance.os}</span>
                                    </div>
                                )}
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-500 dark:text-gray-400">Uptime</span>
                                    <span className="font-mono text-emerald-600 dark:text-emerald-400 font-bold">{formatUptime(instance.uptime_sec)}</span>
                                </div>
                                {selectedNode.upstream_host && (
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-500 dark:text-gray-400">Upstream Host</span>
                                        <span className="font-mono text-gray-700 dark:text-gray-300 text-[10px]">{selectedNode.upstream_host}</span>
                                    </div>
                                )}
                                {selectedNode.upstream_host && (
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-500 dark:text-gray-400">Upstream Status</span>
                                        <span className={`font-bold text-[10px] ${selectedNode.upstream_connected ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                            {selectedNode.upstream_connected ? 'CONNECTED' : 'DISCONNECTED'}
                                        </span>
                                    </div>
                                )}
                                {instance.collected_at && (
                                    <div>
                                        <span className="text-gray-500 dark:text-gray-400 text-[9px] uppercase block">Collected At</span>
                                        <span className="font-mono text-gray-500 dark:text-gray-400 text-[10px]">{formatDate(instance.collected_at)}</span>
                                    </div>
                                )}
                                {instance.last_error != null && instance.last_error !== '' && (
                                    <div className="pt-1.5 border-t border-gray-200 dark:border-gray-700/50">
                                        <span className="text-red-600 dark:text-red-400 text-[9px] uppercase font-bold block">Last Error</span>
                                        <span className="font-mono text-red-600 dark:text-red-400 text-[10px] break-all block mt-0.5">
                                            {typeof instance.last_error === 'string'
                                                ? instance.last_error
                                                : JSON.stringify(instance.last_error)}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* RIGHT DRAWER: TOPOLOGY HISTORY */}
            {isHistoryOpen && (
                <div className="w-[340px] h-full flex flex-col bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-800 overflow-y-auto custom-scroll shadow-2xl z-30 transition-all duration-300 shrink-0">
                    {/* History Sticky Header */}
                    <div className="flex items-center justify-between px-3.5 py-2.5 border-b border-gray-200 dark:border-gray-800 bg-gray-50/90 dark:bg-gray-800/80 backdrop-blur-md sticky top-0 z-10">
                        <div className="flex items-center gap-2">
                            <History className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                            <h2 className="text-xs font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wider">
                                History
                            </h2>
                        </div>
                        <button
                            onClick={() => setIsHistoryOpen(false)}
                            className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white cursor-pointer"
                        >
                            <X className="w-3.5 h-3.5" />
                        </button>
                    </div>

                    <div className="p-3 space-y-3">
                        {historyLoading ? (
                            <div className="py-12 flex flex-col items-center justify-center gap-3">
                                <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
                                <span className="text-xs text-gray-500 dark:text-gray-400">Loading history logs...</span>
                            </div>
                        ) : historyData.length === 0 ? (
                            <div className="py-12 text-center text-xs text-gray-500 italic">
                                No topology history records found.
                            </div>
                        ) : (
                            historyData.map((item, idx) => {
                                const isExpanded = selectedHistoryItem?.id === item.id;
                                const primaryHost =
                                    item.topology?.primary?.host ||
                                    item.topology?.primary?.config_id ||
                                    item.primary_host ||
                                    '—';
                                const statusStr = item.status || item.topology?.status || 'unknown';
                                const statusLower = statusStr.toLowerCase();
                                const isHealthy = statusLower === 'healthy' || statusLower === 'green' || statusLower === 'online';

                                return (
                                    <div
                                        key={item.id || idx}
                                        onClick={() => setSelectedHistoryItem(isExpanded ? null : item)}
                                        className={`bg-gray-50 dark:bg-gray-800/40 border rounded-xl p-3 space-y-2 cursor-pointer transition-all ${isExpanded
                                            ? 'border-orange-500/60 bg-orange-50/40 dark:bg-gray-800/80 shadow-md'
                                            : 'border-gray-200 dark:border-gray-700/60 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-100/70 dark:hover:bg-gray-800/60'
                                            }`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <span className="font-mono text-xs font-bold text-gray-800 dark:text-white bg-gray-200 dark:bg-gray-700/60 px-2 py-0.5 rounded border border-gray-300 dark:border-gray-600/50">
                                                    v{item.version ?? item.id}
                                                </span>
                                                <span
                                                    className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${isHealthy
                                                        ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30'
                                                        : 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30'
                                                        }`}
                                                >
                                                    {statusStr.replace(/_/g, ' ')}
                                                </span>
                                            </div>
                                            <ChevronRight
                                                className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-90 text-orange-600 dark:text-orange-400' : ''
                                                    }`}
                                            />
                                        </div>

                                        {/* History Card Details: status, primary, changed_at */}
                                        <div className="space-y-1 text-xs font-mono pt-1 border-t border-gray-200 dark:border-gray-700/40">
                                            <div className="flex justify-between items-center text-gray-700 dark:text-gray-300">
                                                <span className="text-gray-500 dark:text-gray-400 text-[10px] uppercase">Primary</span>
                                                <span className="font-bold text-blue-600 dark:text-blue-400 truncate max-w-[170px]" title={primaryHost}>
                                                    {primaryHost}
                                                </span>
                                            </div>
                                            <div className="flex justify-between items-center text-gray-700 dark:text-gray-300">
                                                <span className="text-gray-500 dark:text-gray-400 text-[10px] uppercase">Changed At</span>
                                                <span className="text-[10px] text-gray-500 dark:text-gray-400 flex items-center gap-1">
                                                    <Calendar className="w-3 h-3 text-gray-400 dark:text-gray-500" />
                                                    {formatDate(item.changed_at || item.created)}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Expanded Node Breakdown */}
                                        {isExpanded && item.topology?.nodes && (
                                            <div className="pt-2 border-t border-gray-200 dark:border-gray-700/60 space-y-1.5 animate-in fade-in duration-150">
                                                <span className="text-[9px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider block">
                                                    Nodes Snapshot ({item.topology.nodes.length})
                                                </span>
                                                <div className="space-y-1 max-h-40 overflow-y-auto custom-scroll pr-1">
                                                    {item.topology.nodes.map((n: any, nIdx: number) => (
                                                        <div
                                                            key={nIdx}
                                                            className="bg-white dark:bg-gray-900/80 p-1.5 rounded border border-gray-200 dark:border-gray-700/40 text-[10px] font-mono flex items-center justify-between"
                                                        >
                                                            <div className="truncate max-w-[170px]">
                                                                <span className="text-gray-900 dark:text-white font-bold block truncate">{n.host}</span>
                                                                <span className="text-gray-500 text-[9px] uppercase">{n.role}</span>
                                                            </div>
                                                            <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${n.state === 'online' ? 'text-green-600 dark:text-green-400 bg-green-500/10' : 'text-red-600 dark:text-red-400 bg-red-500/10'
                                                                }`}>
                                                                {n.state}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
